import { useVirtualizer } from '@tanstack/react-virtual';
import { ArrowDown, ArrowUp, Check, Columns3, Copy, Download, Link2 } from 'lucide-react';
import {
  Fragment,
  useCallback,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { EMPTY } from '../../lib/format';
import {
  canOfferSelectAllMatching,
  isSelected,
  selectionCount,
  pageCheckboxState,
  selectRange,
  setPageSelected,
  toggleRow,
  type SelectionState,
} from '../../lib/selection';
import { useToasts } from '../../stores/toasts';
import { useUi } from '../../stores/ui';
import { Button } from '../Button';
import { Checkbox, isShiftClick } from '../Checkbox';
import { Popover, usePopover } from '../Popover';
import { Skeleton } from '../Skeleton';
import { BulkActionBar } from './BulkActionBar';
import { downloadCsv, exportFileName, exportValueOf, toCsv, toLabelledText } from './table-export';
import { ColumnsMenuItems } from './ColumnsMenu';
import type { TableViewsConfig } from './TableViews';
import styles from './DataTable.module.css';
import type { Column, SortSpec } from './types';
import { MIN_WIDTH, useTablePreferences, widthOf, type TablePreferences } from './use-table-preferences';
import { ToneIcon, toneClass, type Tone } from '../Tone';
import { useToneArea } from '../ToneArea';

// The data table. Roughly 40 screens use it.
//
// Adapted from Tomo's `SheetTable.tsx` — 1519 lines hand-rolled on
// @tanstack/react-virtual only, never on @tanstack/react-table (TECH-STACK 1.3) —
// and restyled entirely on the Flock tokens. Their global `zoom` trick is
// deliberately NOT carried over; it taxes every pointer calculation in the file.
//
// It is a real <table>, not a grid of divs, because `position: sticky` on a <th>
// and a <td> is what gives a sticky header AND a sticky first column on both axes
// with no scroll listener and no measurement. Virtualization is two spacer rows,
// above and below the rendered window, so the scrollbar geometry stays honest
// without absolute positioning fighting the sticky cells.

const ROW_OVERSCAN = 10;
const COMFORTABLE_ROW_H = 40;
const COMPACT_ROW_H = 32;
/** Below this many columns there is nothing to scroll, so nothing needs pinning. */
const STICKY_IDENTITY_FROM_COLUMNS = 5;
/** How long a copied row's button shows its tick before going back to the copy icon. */
const COPIED_MARK_MS = 1500;

/** A text field, a list box or anything editable: keys there belong to it. */
function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

/** One letter, no modifier: what a screen's row keys may be (ADR 0049). */
const LETTER_KEY = /^[a-z]$/i;

interface DataTableProps<TRow> {
  /** Namespaces the per-screen column preferences. */
  readonly screenId: string;
  readonly columns: readonly Column<TRow>[];
  readonly rows: readonly TRow[];
  readonly getRowId: (row: TRow) => string;
  /** The filtered total from the server — what "all N matching" counts. */
  readonly total?: number;
  readonly sort: readonly SortSpec[];
  readonly onSortChange: (sort: readonly SortSpec[]) => void;
  readonly selection: SelectionState;
  readonly onSelectionChange: (selection: SelectionState) => void;
  /** Enter on the focused row. A click opens a record only through its identity column's link. */
  readonly onOpenRow?: (row: TRow) => void;
  /** The two or three commonest actions, revealed on row hover (06-qol §3). */
  readonly rowActions?: (row: TRow) => ReactNode;
  /**
   * A screen's one-letter actions on the focused row (ADR 0049: RNK-01's a, h, r, m). Called for an
   * unmodified letter; return true when the screen used it. `rowElement` anchors a pop-up to the row.
   */
  readonly onRowKey?: (row: TRow, key: string, rowElement: HTMLElement | null) => boolean;
  /**
   * A full-width row opened under one row (ADR 0049: RNK-01's remark box, as in its mockup). The rows
   * below move down while it is open; it stays in view as the table scrolls sideways.
   */
  readonly expandedRowId?: string | null;
  readonly renderExpanded?: (row: TRow) => ReactNode;
  readonly bulkActions?: ReactNode;
  /**
   * Column state owned by the screen, when something other than the table changes it —
   * a saved view choosing columns (S16). Otherwise the table keeps its own.
   */
  readonly preferences?: TablePreferences;
  /** Only for data that has never been on screen (06-qol §5, CLAUDE.md §5a rule 2). */
  readonly isLoading?: boolean;
  readonly emptyState?: ReactNode;
  readonly label: string;
  /**
   * False for a list whose rows are never acted on together (S18's exam list): no tick
   * boxes, and Space, shift- and ctrl-click select nothing. The gutter stays, so the
   * columns line up with every other table.
   */
  readonly isSelectable?: boolean;
  /**
   * S32 (Q27): each row's own address. With it, a selection offers "Copy links" and "Export
   * selected" before the screen's own bulk actions; `exportName` names the file.
   */
  readonly linkOf?: (row: TRow) => string;
  readonly exportName?: string;
  /** S32 (Q28): named views of this table's filters and columns, in its columns menu. */
  readonly views?: TableViewsConfig;
  /**
   * A row's status or stage tone, shown as the icon tile before its first column's value
   * (ADR 0035). Without it, or where it returns undefined, the row has no tile (ADR 0052).
   */
  readonly toneOf?: (row: TRow) => Tone | undefined;
  /**
   * How `toneOf` marks the row: ADR 0052's icon tile, or a small dot where a screen's mockup has
   * one (JOB-01, Jobs.dc.html). Opt-in, so every other table keeps the tile.
   */
  readonly toneMark?: 'tile' | 'dot';
  /**
   * False when the screen puts the columns menu in its own toolbar (`ColumnsMenuButton`, with the
   * same `preferences`). The header keeps its last cell, so the columns still line up.
   */
  readonly isColumnMenuInHeader?: boolean;
  /** What the table lists, where that differs from its area (ADR 0035). */
  readonly tone?: Tone;
}

export function DataTable<TRow>({
  screenId,
  columns,
  rows,
  getRowId,
  total,
  sort,
  onSortChange,
  selection,
  isSelectable = true,
  onSelectionChange,
  onOpenRow,
  rowActions,
  onRowKey,
  expandedRowId = null,
  renderExpanded,
  bulkActions,
  preferences: screenPreferences,
  isLoading = false,
  emptyState,
  label,
  linkOf,
  exportName,
  toneOf,
  toneMark = 'tile',
  isColumnMenuInHeader = true,
  tone: tableTone,
  views,
}: DataTableProps<TRow>): ReactNode {
  const showToast = useToasts((state) => state.show);
  const area = useToneArea();
  const density = useUi((state) => state.density);
  const ownPreferences = useTablePreferences(screenId, columns);
  const preferences = screenPreferences ?? ownPreferences;
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [focusedRow, setFocusedRow] = useState<number | null>(null);
  const [resizingColumn, setResizingColumn] = useState<string | null>(null);
  const [copiedRowId, setCopiedRowId] = useState<string | null>(null);
  const {
    isOpen: isColumnMenuOpen,
    anchor: columnMenuAnchor,
    triggerRef: columnMenuTrigger,
    toggle: toggleColumnMenu,
    close: closeColumnMenu,
  } = usePopover();

  const visibleColumns = useMemo(
    () => columns.filter((column) => !preferences.hiddenColumns.has(column.id)),
    [columns, preferences.hiddenColumns],
  );

  const pageIds = useMemo(() => rows.map(getRowId), [rows, getRowId]);
  const rowHeight = density === 'compact' ? COMPACT_ROW_H : COMFORTABLE_ROW_H;

  // @tanstack/react-virtual returns functions the React Compiler cannot memoize,
  // so it opts this component out of auto-memoization. Accepted deliberately: it
  // is the only thing keeping a 30k-row table alive (TECH-STACK 3.4), and this
  // component is hand-tuned rather than relying on the compiler.
  // oxlint-disable-next-line react/incompatible-library
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    overscan: ROW_OVERSCAN,
  });
  const virtualRows = virtualizer.getVirtualItems();

  // Spacer heights, so the scrollbar reflects the full list rather than only what
  // is painted.
  const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? virtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end
      : 0;

  /** Sticky offsets, measured from live widths. Geometry, not appearance. */
  const identityLeft = useMemo(() => {
    const identityIndex = visibleColumns.findIndex((column) => column.isIdentity);
    return identityIndex === 0 ? true : false;
  }, [visibleColumns]);

  const isIdentitySticky =
    identityLeft && visibleColumns.length >= STICKY_IDENTITY_FROM_COLUMNS;

  const sortFor = useCallback(
    (columnId: string): { direction: 'asc' | 'desc'; rank: number } | null => {
      const index = sort.findIndex((spec) => spec.columnId === columnId);
      if (index < 0) return null;
      return { direction: sort[index].direction, rank: index + 1 };
    },
    [sort],
  );

  const onHeaderClick = useCallback(
    (column: Column<TRow>, isShift: boolean): void => {
      if (column.isSortable !== true) return;
      const existing = sort.find((spec) => spec.columnId === column.id);
      const flipped: SortSpec = {
        columnId: column.id,
        direction: existing?.direction === 'asc' ? 'desc' : 'asc',
      };

      // Shift-click appends, building a multi-column sort. A plain click replaces
      // it, because that is what the user means nine times out of ten.
      if (!isShift) {
        // A third click on the only sorted column clears the sort entirely.
        if (existing?.direction === 'desc') {
          onSortChange([]);
          return;
        }
        onSortChange([flipped]);
        return;
      }
      onSortChange(
        existing
          ? sort.map((spec) => (spec.columnId === column.id ? flipped : spec))
          : [...sort, flipped],
      );
    },
    [sort, onSortChange],
  );

  const startResize = useCallback(
    (event: ReactPointerEvent, column: Column<TRow>): void => {
      event.preventDefault();
      event.stopPropagation();
      const startX = event.clientX;
      const startWidth = widthOf(column, preferences.widths);
      setResizingColumn(column.id);

      const onMove = (moveEvent: PointerEvent): void => {
        preferences.setWidth(column.id, Math.max(MIN_WIDTH, startWidth + (moveEvent.clientX - startX)));
      };
      const onUp = (): void => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        setResizingColumn(null);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [preferences],
  );

  const onRowClick = useCallback(
    (rowIndex: number, event: React.MouseEvent): void => {
      const id = pageIds[rowIndex];
      setFocusedRow(rowIndex);
      if (isSelectable && event.shiftKey) {
        onSelectionChange(selectRange(selection, id, pageIds));
        return;
      }
      if (isSelectable && (event.metaKey || event.ctrlKey)) {
        onSelectionChange(toggleRow(selection, id));
      }
      // A plain click opens nothing (DECISIONS-LOG 2026-10-06): only the identity column's
      // link opens the record, so people can select and copy text from any other cell.
    },
    [pageIds, selection, onSelectionChange, isSelectable],
  );

  /** What a bulk action acts on: the selected rows that are loaded on screen. */
  const selectedRows = (): readonly TRow[] => rows.filter((row) => isSelected(selection, getRowId(row)));

  const loadedNote = (count: number): string | undefined => {
    const selected = selectionCount(selection, total);
    return selected !== undefined && selected > count ? `The ${count} loaded on screen — scroll or page to include the rest.` : undefined;
  };

  async function copyLinks(): Promise<void> {
    if (!linkOf) return;
    const picked = selectedRows();
    try {
      await navigator.clipboard.writeText(picked.map((row) => new URL(linkOf(row), document.baseURI).href).join('\n'));
      showToast({ tone: 'success', message: `${picked.length} ${picked.length === 1 ? 'link' : 'links'} copied`, detail: loadedNote(picked.length) });
    } catch {
      showToast({ tone: 'danger', message: "The links weren't copied", detail: 'This browser blocked the clipboard. Try again from the page itself.' });
    }
  }

  /** DECISIONS-LOG 2026-10-06: the row as shown — its visible columns, in their order. */
  async function copyRow(row: TRow, id: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(toLabelledText(visibleColumns, row));
      setCopiedRowId(id);
      setTimeout(() => setCopiedRowId((current) => (current === id ? null : current)), COPIED_MARK_MS);
      showToast({ tone: 'success', message: 'Row copied' });
    } catch {
      showToast({ tone: 'danger', message: "The row wasn't copied", detail: 'This browser blocked the clipboard. Try again from the page itself.' });
    }
  }

  function exportSelected(): void {
    if (!exportName) return;
    const picked = selectedRows();
    downloadCsv(exportFileName(exportName), toCsv(visibleColumns.filter((column) => picked.some((row) => exportValueOf(column, row) !== '')), picked));
    showToast({ tone: 'success', message: `${picked.length} ${picked.length === 1 ? 'row' : 'rows'} exported`, detail: loadedNote(picked.length) });
  }

  // 06-qol §2: every list is arrow-key navigable, Enter opens the focused row and
  // Space toggles its selection.
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent): void => {
      if (rows.length === 0) return;
      // Typing in a field inside the table (an opened row's remark box) is typing, not a row command.
      if (isEditable(event.target)) return;
      const current = focusedRow ?? -1;

      const moveTo = (next: number): void => {
        event.preventDefault();
        const clamped = Math.max(0, Math.min(next, rows.length - 1));
        setFocusedRow(clamped);
        virtualizer.scrollToIndex(clamped, { align: 'auto' });
      };

      if (event.key === 'ArrowDown') return moveTo(current + 1);
      if (event.key === 'ArrowUp') return moveTo(current - 1);
      if (event.key === 'Home') return moveTo(0);
      if (event.key === 'End') return moveTo(rows.length - 1);
      if (current < 0) return;

      const focused = rows[current];
      if (onRowKey && focused !== undefined && LETTER_KEY.test(event.key) && !event.ctrlKey && !event.metaKey && !event.altKey) {
        const id = pageIds[current] ?? '';
        const element = scrollRef.current?.querySelector<HTMLElement>(`tr[data-row-id="${CSS.escape(id)}"]`) ?? null;
        if (onRowKey(focused, event.key.toLowerCase(), element)) event.preventDefault();
        return;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        onOpenRow?.(rows[current]);
        return;
      }
      if (event.key === ' ' && isSelectable) {
        event.preventDefault();
        onSelectionChange(toggleRow(selection, pageIds[current]));
      }
    },
    [rows, focusedRow, virtualizer, onOpenRow, onRowKey, onSelectionChange, selection, pageIds, isSelectable],
  );

  const headerCheckbox = pageCheckboxState(selection, pageIds);
  const showEmpty = !isLoading && rows.length === 0;

  return (
    <>
      <div
        ref={scrollRef}
        className={`${toneClass} ${styles.wrap}`}
        data-expandable={renderExpanded ? true : undefined}
        data-tone={tableTone ?? area.tone}
        data-density={density}
        data-has-bar={!isSelectionHidden(selection) || undefined}
        tabIndex={0}
        role="region"
        aria-label={label}
        onKeyDown={onKeyDown}
      >
        <table className={styles.table}>
          <colgroup>
            <col style={LEAD_COL} />
            {visibleColumns.map((column) => (
              <col key={column.id} style={{ width: widthOf(column, preferences.widths) }} />
            ))}
            <col style={GUTTER_COL} />
          </colgroup>

          <thead className={styles.head}>
            <tr>
              <th className={styles.gutter} scope="col">
                {isSelectable && (
                <div className={styles.checkbox}>
                  <Checkbox
                    checked={headerCheckbox === 'checked'}
                    isIndeterminate={headerCheckbox === 'indeterminate'}
                    onChange={(checked) =>
                      onSelectionChange(setPageSelected(selection, pageIds, checked))
                    }
                    ariaLabel={
                      headerCheckbox === 'checked'
                        ? `Deselect all ${rows.length} rows on this page`
                        : `Select all ${rows.length} rows on this page`
                    }
                  />
                  <span className={styles.copySpacer} aria-hidden />
                </div>
                )}
              </th>

              {visibleColumns.map((column, index) => {
                const active = sortFor(column.id);
                const sticky = isIdentitySticky && index === 0;
                const style: CSSProperties | undefined = sticky
                  ? { left: LEAD_WIDTH }
                  : undefined;
                return (
                  <th
                    key={column.id}
                    scope="col"
                    className={sticky ? `${styles.sticky} ${styles.stickyEdge}` : undefined}
                    style={style}
                    aria-sort={
                      active === null
                        ? undefined
                        : active.direction === 'asc'
                          ? 'ascending'
                          : 'descending'
                    }
                  >
                    <div className={styles.headCell}>
                      <button
                        type="button"
                        className={styles.headButton}
                        onClick={(event) => onHeaderClick(column, event.shiftKey)}
                        title={
                          column.headerTitle ??
                          (column.isSortable === true
                            ? `Sort by ${column.header}. Shift-click to add a second sort`
                            : column.header)
                        }
                        aria-label={column.headerContent === undefined ? undefined : column.header}
                      >
                        <span className={styles.headLabel}>{column.headerContent ?? column.header}</span>
                        {active !== null && (
                          <span className={styles.sortMark} aria-hidden>
                            {active.direction === 'asc' ? (
                              <ArrowUp size={12} />
                            ) : (
                              <ArrowDown size={12} />
                            )}
                            {sort.length > 1 && (
                              <span className={styles.sortRank}>{active.rank}</span>
                            )}
                          </span>
                        )}
                      </button>
                      <span
                        className={`${styles.resizeHandle} ${
                          resizingColumn === column.id ? styles.resizeHandleActive : ''
                        }`}
                        onPointerDown={(event) => startResize(event, column)}
                        aria-hidden
                      />
                    </div>
                  </th>
                );
              })}

              <th className={styles.headEnd} scope="col">
                {isColumnMenuInHeader && (
                <button
                  ref={columnMenuTrigger}
                  type="button"
                  className={styles.headButton}
                  onClick={toggleColumnMenu}
                  aria-label={views ? 'Columns and saved views' : 'Choose columns'}
                  aria-haspopup="menu"
                  aria-expanded={isColumnMenuOpen}
                >
                  <Columns3 size={14} aria-hidden />
                </button>
                )}
              </th>
            </tr>
          </thead>

          <tbody className={styles.body}>
            {paddingTop > 0 && (
              <tr className={styles.spacer} aria-hidden>
                <td colSpan={visibleColumns.length + 2} style={{ height: paddingTop }} />
              </tr>
            )}

            {isLoading &&
              rows.length === 0 &&
              Array.from({ length: 12 }, (_, index) => (
                <tr key={`skeleton-${index}`} aria-hidden>
                  <td className={styles.gutter} />
                  {visibleColumns.map((column) => (
                    <td key={column.id}>
                      <Skeleton />
                    </td>
                  ))}
                  <td />
                </tr>
              ))}

            {virtualRows.map((virtualRow) => {
              const row = rows[virtualRow.index];
              const id = pageIds[virtualRow.index];
              const rowSelected = isSelected(selection, id);
              const tone = toneOf && row !== undefined ? toneOf(row) : undefined;
              const classes = [
                styles.row,
                rowSelected ? styles.rowSelected : '',
                focusedRow === virtualRow.index ? styles.rowFocused : '',
              ]
                .filter(Boolean)
                .join(' ');

              return (
                <Fragment key={id}>
                <tr
                  data-row-id={id}
                  className={classes}
                  aria-selected={rowSelected}
                  onClick={(event) => onRowClick(virtualRow.index, event)}
                >
                  <td className={styles.gutter}>
                    <div
                      className={styles.checkbox}
                      // Keeps the click from also opening the row.
                      onClick={(event) => event.stopPropagation()}
                      role="presentation"
                    >
                      {isSelectable && (
                      <Checkbox
                        checked={rowSelected}
                        // Users reach for the checkbox, so handling ranges only on
                        // the row body would silently select one row where they
                        // expected seven.
                        onChange={(_checked, event) =>
                          onSelectionChange(
                            isShiftClick(event)
                              ? selectRange(selection, id, pageIds)
                              : toggleRow(selection, id),
                          )
                        }
                        ariaLabel={`Select row ${virtualRow.index + 1}`}
                      />
                      )}
                      <button
                        type="button"
                        className={styles.copyButton}
                        data-copied={copiedRowId === id}
                        onClick={() => void copyRow(row, id)}
                        aria-label={`Copy row ${virtualRow.index + 1}`}
                        title="Copy row"
                      >
                        {copiedRowId === id ? <Check size={14} aria-hidden /> : <Copy size={14} aria-hidden />}
                      </button>
                    </div>
                  </td>

                  {visibleColumns.map((column, index) => {
                    const sticky = isIdentitySticky && index === 0;
                    const content = column.cell(row);
                    const isEmptyValue = content === null || content === undefined || content === '';
                    const value = isEmptyValue ? EMPTY : content;
                    const classNames = [
                      column.isNumeric === true ? styles.numeric : '',
                      sticky ? `${styles.sticky} ${styles.stickyEdge}` : '',
                      isEmptyValue ? styles.empty : '',
                    ]
                      .filter(Boolean)
                      .join(' ');

                    return (
                      <td
                        key={column.id}
                        className={classNames === '' ? undefined : classNames}
                        style={sticky ? { left: LEAD_WIDTH } : undefined}
                      >
                        {/* An empty value is ALWAYS an em dash (06-qol §3). */}
                        {index === 0 && tone ? (
                          <span className={styles.toneCell}>
                            {toneMark === 'dot' ? (
                              <span className={`${toneClass} ${styles.toneDot}`} data-tone={tone} aria-hidden />
                            ) : (
                              <ToneIcon tone={tone} size="sm" />
                            )}
                            <span className={styles.toneCellValue}>{value}</span>
                          </span>
                        ) : (
                          value
                        )}
                      </td>
                    );
                  })}

                  <td>
                    {rowActions && (
                      <span
                        className={styles.rowActions}
                        onClick={(event) => event.stopPropagation()}
                        role="presentation"
                      >
                        {rowActions(row)}
                      </span>
                    )}
                  </td>
                </tr>
                {renderExpanded && expandedRowId === id && row !== undefined && (
                  <tr className={styles.expandedRow}>
                    <td colSpan={visibleColumns.length + 2}>
                      <div className={styles.expandedInner}>{renderExpanded(row)}</div>
                    </td>
                  </tr>
                )}
                </Fragment>
              );
            })}

            {paddingBottom > 0 && (
              <tr className={styles.spacer} aria-hidden>
                <td colSpan={visibleColumns.length + 2} style={{ height: paddingBottom }} />
              </tr>
            )}
          </tbody>
        </table>

        {showEmpty && emptyState}

        {!isSelectionHidden(selection) && (
          <BulkActionBar
            selection={selection}
            total={total}
            canSelectAllMatching={canOfferSelectAllMatching(selection, pageIds, total)}
            onSelectionChange={onSelectionChange}
          >
            {linkOf && exportName && (
              <>
                <Button size="sm" variant="ghost" icon={Link2} onClick={() => void copyLinks()}>
                  Copy links
                </Button>
                <Button size="sm" variant="ghost" icon={Download} onClick={exportSelected}>
                  Export selected
                </Button>
              </>
            )}
            {bulkActions}
          </BulkActionBar>
        )}
      </div>

      <Popover
        anchor={columnMenuAnchor}
        onClose={closeColumnMenu}
        align="right"
        label={views ? 'Columns and saved views' : 'Choose columns'}
      >
        <ColumnsMenuItems columns={columns} preferences={preferences} views={views} onDone={closeColumnMenu} />
      </Popover>
    </>
  );
}

function isSelectionHidden(selection: SelectionState): boolean {
  return selection.mode === 'none';
}

/** The leading gutter — tick box and copy-row button. Fixed, and the sticky origin. Matches `--nr-row-lead-w`. */
const LEAD_WIDTH = 64;
const LEAD_COL: CSSProperties = { width: LEAD_WIDTH };
/** The trailing gutter — the columns menu. Matches `--nr-row-gutter-w`. */
const GUTTER_WIDTH = 44;
const GUTTER_COL: CSSProperties = { width: GUTTER_WIDTH };
